/*
  Queen Tape — slide-out cart drawer
  Pairs with sections/qt-cart-drawer.liquid and assets/qt-cart-drawer.css.

  What it does
  - Opens the drawer when anything is added to the cart and when the cart icon is tapped.
    The theme's own add-to-cart (Shrine product forms, bundles, quantity breaks, Buy Now)
    still does the adding; this drawer just opens instead of the theme's drawer.
  - Re-renders the drawer through Shopify's Section Rendering API after every change,
    so all prices and totals come from Shopify, not from this script.
  - Quantity +/-, remove, clear cart and one-tap add-ons.

  Use it from your own code:
    window.QTCart.open()   window.QTCart.close()   window.QTCart.refresh({ open: true })
*/
(() => {
  if (window.QTCart) return;

  const SECTION = 'qt-cart-drawer';
  const CART_URL_RE = /\/cart\/(add|change|update|clear)(?:\.js|\.json)?(?:[?#]|$)/;
  const nativeFetch = window.fetch.bind(window);

  let busyCount = 0;
  let lastTrigger = null;
  let clearTimer = null;
  let externalTimer = null;
  let externalOpen = false;

  const drawer = () => document.getElementById('QTCartDrawer');
  const panel = () => drawer()?.querySelector('.qt-cd__panel');
  const enabled = () => {
    const d = drawer();
    return !!d && d.dataset.replace !== 'false';
  };
  const root = () => {
    const d = drawer();
    let r = (d && d.dataset.root) || window.Shopify?.routes?.root || '/';
    return r.endsWith('/') ? r : `${r}/`;
  };

  function readState() {
    const el = drawer()?.querySelector('[data-qt-state]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  async function api(path, body) {
    const opts = { method: body ? 'POST' : 'GET', headers: { Accept: 'application/json' }, credentials: 'same-origin' };
    if (body instanceof FormData) {
      opts.body = body;
    } else if (body) {
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    const res = await nativeFetch(root() + path, opts);
    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      /* empty body */
    }
    if (!res.ok) {
      const msg = data && (typeof data.description === 'string' ? data.description : data.message);
      throw new Error(msg || 'Something went wrong. Please try again.');
    }
    return data;
  }

  function setBusy(on) {
    busyCount = Math.max(0, busyCount + (on ? 1 : -1));
    const p = panel();
    if (p) p.setAttribute('aria-busy', busyCount > 0 ? 'true' : 'false');
  }

  function showNotice(msg) {
    const n = drawer()?.querySelector('[data-qt-notice]');
    if (!n) return;
    n.textContent = msg;
    n.hidden = false;
    clearTimeout(showNotice.t);
    showNotice.t = setTimeout(hideNotice, 7000);
  }
  function hideNotice() {
    const n = drawer()?.querySelector('[data-qt-notice]');
    if (n) n.hidden = true;
  }

  /* ---------- Rendering ---------- */

  async function render() {
    const res = await nativeFetch(`${root()}?sections=${SECTION},cart-icon-bubble`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error(`Section render failed (${res.status})`);
    const json = await res.json();
    const d = drawer();
    if (!d || !json[SECTION]) return;

    const doc = new DOMParser().parseFromString(json[SECTION], 'text/html');

    const fresh = doc.querySelector('[data-qt-cart-contents]');
    const current = d.querySelector('[data-qt-cart-contents]');
    if (fresh && current) current.innerHTML = fresh.innerHTML;

    // Header cart count (Dawn-style themes, including Shrine).
    const bubbleHtml = json['cart-icon-bubble'];
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubbleHtml && bubble) {
      const b = new DOMParser().parseFromString(bubbleHtml, 'text/html');
      const src = b.querySelector('.shopify-section') || b.body;
      bubble.innerHTML = src.innerHTML;
    }

    const state = readState();
    if (state) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = state.itemCount;
      });
      document.dispatchEvent(new CustomEvent('qt:cart:updated', { detail: state }));
    }
  }

  async function refresh({ open: shouldOpen = false, quiet = false } = {}) {
    if (!quiet) setBusy(true);
    try {
      await render();
    } catch (e) {
      console.error('[QT cart]', e);
    } finally {
      if (!quiet) setBusy(false);
    }
    if (shouldOpen) open();
  }

  async function mutate(fn) {
    if (busyCount > 0) return;
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      showNotice(e.message);
    }
    await refresh();
    setBusy(false);
  }

  /* ---------- Open / close ---------- */

  function open(trigger) {
    const d = drawer();
    if (!d) return;
    if (trigger) lastTrigger = trigger;
    else if (!d.classList.contains('is-open')) lastTrigger = document.activeElement;
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('qt-cd-lock');
    // If a theme drawer slipped open underneath, undo its scroll lock.
    document.body.classList.remove('overflow-hidden');
    requestAnimationFrame(() => panel()?.focus({ preventScroll: true }));
  }

  function close() {
    const d = drawer();
    if (!d || !d.classList.contains('is-open')) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('qt-cd-lock');
    hideNotice();
    if (lastTrigger && typeof lastTrigger.focus === 'function' && document.contains(lastTrigger)) {
      try {
        lastTrigger.focus({ preventScroll: true });
      } catch (e) {
        /* ignore */
      }
    }
  }

  /* ---------- Taking over add-to-cart ---------- */

  function isCartLink(a) {
    if (!a || !a.href) return false;
    try {
      const u = new URL(a.href, location.href);
      return u.origin === location.origin && /\/cart\/?$/.test(u.pathname);
    } catch (e) {
      return false;
    }
  }

  function isAddForm(form) {
    if (!(form instanceof HTMLFormElement) || form.hasAttribute('data-qt-ignore')) return false;
    try {
      const u = new URL(form.getAttribute('action') || '', location.href);
      return /\/cart\/add(?:\.js)?\/?$/.test(u.pathname);
    } catch (e) {
      return false;
    }
  }

  function setButtonBusy(btn, on) {
    if (!btn) return;
    btn.classList.toggle('qt-cd-busy', on);
    if (on) btn.setAttribute('aria-busy', 'true');
    else btn.removeAttribute('aria-busy');
  }

  // Cart icon / cart links -> open drawer (capture phase, so it runs before theme code).
  document.addEventListener(
    'click',
    (e) => {
      if (!enabled() || e.defaultPrevented) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button > 0) return;
      const el = e.target.closest('[data-qt-cart-open], #cart-icon-bubble, a[href]');
      if (!el || el.closest('#QTCartDrawer')) return;
      if (!el.matches('[data-qt-cart-open], #cart-icon-bubble') && !isCartLink(el)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      open(el);
      refresh({ quiet: true });
    },
    true
  );

  // Plain add-to-cart forms -> add with AJAX, then open drawer.
  // Runs in the bubble phase and skips forms the theme already handles itself
  // (Shrine/Dawn product forms, bundles, quantity breaks, Buy Now), so those keep
  // working exactly as before and simply open this drawer when they finish.
  document.addEventListener('submit', async (e) => {
    if (!enabled() || e.defaultPrevented) return;
    const form = e.target;
    if (!isAddForm(form)) return;
    e.preventDefault();

    const btn = e.submitter || form.querySelector('[type="submit"]');
    if (btn && (btn.classList.contains('qt-cd-busy') || btn.getAttribute('aria-disabled') === 'true')) return;
    setButtonBusy(btn, true);
    try {
      const fd = new FormData(form);
      if (!fd.get('id')) throw new Error('Please choose an option first.');
      await api('cart/add.js', fd);
      await refresh();
      open(btn);
    } catch (err) {
      await refresh();
      open(btn);
      showNotice(err.message);
    } finally {
      setButtonBusy(btn, false);
    }
  });

  // Any other code that changes the cart (theme quick-add, custom buttons, apps).
  function onExternalChange(isAdd) {
    if (!enabled()) return;
    externalOpen = externalOpen || isAdd;
    clearTimeout(externalTimer);
    externalTimer = setTimeout(() => {
      const shouldOpen = externalOpen;
      externalOpen = false;
      refresh({ open: shouldOpen, quiet: !shouldOpen });
    }, 60);
  }

  window.fetch = function (input, init) {
    const result = nativeFetch(input, init);
    try {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input && input.url) || '';
      const m = CART_URL_RE.exec(url);
      if (m) {
        result
          .then((res) => {
            if (res.ok) onExternalChange(m[1] === 'add');
          })
          .catch(() => {});
      }
    } catch (e) {
      /* never break the page's own fetch */
    }
    return result;
  };

  const xhrOpen = XMLHttpRequest.prototype.open;
  const xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__qtUrl = String(url);
    return xhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    const m = CART_URL_RE.exec(this.__qtUrl || '');
    if (m) {
      this.addEventListener('load', () => {
        if (this.status >= 200 && this.status < 300) onExternalChange(m[1] === 'add');
      });
    }
    return xhrSend.apply(this, arguments);
  };

  // Dawn / Shrine: route the theme's own drawer and popup to this drawer.
  ['cart-drawer', 'cart-notification'].forEach((tag) => {
    customElements.whenDefined(tag).then(() => {
      const C = customElements.get(tag);
      if (!C || C.prototype.__qtPatched) return;
      const original = C.prototype.open;
      C.prototype.open = function () {
        if (enabled()) {
          onExternalChange(true);
          return;
        }
        return original && original.apply(this, arguments);
      };
      C.prototype.__qtPatched = true;
    });
  });

  /* ---------- Clicks inside the drawer ---------- */

  document.addEventListener('click', (e) => {
    const d = drawer();
    if (!d || !d.contains(e.target)) return;

    if (e.target.closest('[data-qt-close]')) {
      e.preventDefault();
      close();
      return;
    }

    const qtyBtn = e.target.closest('[data-qt-qty]');
    if (qtyBtn) {
      e.preventDefault();
      const quantity = Math.max(0, parseInt(qtyBtn.dataset.qtQty, 10) || 0);
      mutate(() => api('cart/change.js', { id: qtyBtn.dataset.key, quantity }));
      return;
    }

    const addBtn = e.target.closest('[data-qt-add]');
    if (addBtn) {
      e.preventDefault();
      addBtn.classList.add('is-loading');
      mutate(() => api('cart/add.js', { items: [{ id: Number(addBtn.dataset.qtAdd), quantity: 1 }] }));
      return;
    }

    const clearBtn = e.target.closest('[data-qt-clear]');
    if (clearBtn) {
      e.preventDefault();
      if (clearTimer) {
        clearTimeout(clearTimer);
        clearTimer = null;
        mutate(() => api('cart/clear.js', {}));
      } else {
        const label = clearBtn.textContent;
        clearBtn.textContent = 'Tap again to clear';
        clearTimer = setTimeout(() => {
          clearTimer = null;
          clearBtn.textContent = label;
        }, 3000);
      }
    }
  });

  /* ---------- Keyboard ---------- */

  document.addEventListener('keydown', (e) => {
    const d = drawer();
    if (!d || !d.classList.contains('is-open')) return;
    if (e.key === 'Escape') {
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusables = [...d.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter(
      (el) => el.offsetParent !== null || el === document.activeElement
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === panel())) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  /* ---------- Page lifecycle + theme editor ---------- */

  function markReady() {
    document.documentElement.classList.toggle('qt-cd-ready', enabled());
  }
  markReady();

  window.addEventListener('pageshow', (e) => {
    if (e.persisted) refresh({ quiet: true });
  });

  // Theme editor: open the drawer while its section or one of its add-on blocks is selected,
  // and keep it open when the editor re-renders the section after a settings change.
  let editorOpen = false;
  document.addEventListener('shopify:section:load', (e) => {
    if (e.detail?.sectionId !== SECTION) return;
    markReady();
    if (editorOpen) open();
  });
  ['shopify:section:select', 'shopify:block:select'].forEach((type) =>
    document.addEventListener(type, (e) => {
      if (e.detail?.sectionId !== SECTION) return;
      editorOpen = true;
      open();
    })
  );
  document.addEventListener('shopify:section:deselect', (e) => {
    if (e.detail?.sectionId !== SECTION) return;
    editorOpen = false;
    close();
  });

  window.QTCart = {
    open: () => open(),
    close,
    refresh: (opts) => refresh(opts),
  };
})();
